// What makes this app dubai-todo.com rather than another *-todo site — the
// counterpart of apps/riviera/src/site.ts. @todo/core reads it as the `@site`
// alias (tsconfig.json). Dependency-free: imported from anywhere.

import type { SiteConfig } from '@todo/core/site-config';

export const SITE = {
  // Short machine name (the Worker's): prefixes this site's browser storage keys.
  id: 'dubai-todo',

  // Absolute origin baked into canonical / og:url tags. Override
  // per-environment with EXPO_PUBLIC_SITE_URL if needed.
  url: (process.env.EXPO_PUBLIC_SITE_URL ?? 'https://dubai-todo.com').replace(/\/+$/, ''),
  name: 'Dubai To-Do',

  // UI languages, in switcher order. Each is a URL prefix (/en, /de, /ru).
  locales: ['en', 'de', 'ru'],
  defaultLocale: 'en',

  timeZone: 'Asia/Dubai',
  // sync-viator's default request currency (and Click&Boat's): the fallback
  // for a row without one. Revisit with the partnerships (AED?).
  currency: 'EUR',

  // `cities.id` values the site covers (supabase/sites/dubai.sql). One city:
  // the areas (Downtown, Marina, Palm…) are collections, not cities — see
  // src/lib/collections.ts.
  cityIds: ['dubai'],

  // Top-level sections, in header order. Each is a /[lang]/<section> route.
  sections: ['tours', 'boats', 'events'],
  // Tour pages list Viator's product facts (included, pickup, know before you
  // go) — the project has the viator_product_details feature (supabase/sites/dubai.sql).
  tourDetails: true,

  // ISO 3166 country of the suppliers' region (structured data).
  country: 'AE',
  // Event JSON-LD (seo/event-jsonld.ts): Platinumlist's venue address is a real
  // street line and its venues carry coordinates; every date says whether it
  // is sold out (sync-platinumlist keeps it in raw).
  eventAddress: { streetAddress: true, geo: true },
  eventSoldOut: true,

  regionName: {
    en: 'Dubai',
    de: 'Dubai',
    ru: 'Дубай',
  },
  description: {
    en: 'Things to do in Dubai — tours, desert safaris, dhow cruises, yacht charters and day trips, from Downtown and Dubai Marina to Palm Jumeirah.',
    de: 'Erlebnisse in Dubai — Touren, Wüstensafaris, Dhau-Fahrten, Yachtcharter und Tagesausflüge, von Downtown und Dubai Marina bis Palm Jumeirah.',
    ru: 'Чем заняться в Дубае — экскурсии, сафари в пустыне, круизы на доу, аренда яхт и поездки на день: от Даунтауна и Дубай Марины до Пальмы Джумейра.',
  },
  // Breadcrumb labels for the JSON-LD built outside React: keep in sync with
  // `nav.*` in src/lib/i18n/resources/*.json.
  crumbs: {
    tours: { en: 'Tours', de: 'Touren', ru: 'Экскурсии' },
    boats: { en: 'Yachts & Boats', de: 'Yachten & Boote', ru: 'Яхты и лодки' },
    articles: { en: 'Guides', de: 'Reiseführer', ru: 'Путеводитель' },
    events: { en: 'Events', de: 'Events', ru: 'События' },
  },

  // "Where to stay" searches (@todo/core/hotels, @todo/web-ui/components/hotels).
  stay22: {
    /** Stay22 affiliate id — public, it's in every link. riviera's account: the campaigns tell the sites apart. */
    aid: 'rivieratodo',
    /** Campaign suffix for searches covering the whole destination, not one city. */
    region: 'dubai',
    /** --primary of src/global.css. */
    mapColor: '7a5434',
    // `cities.centroid` of supabase/sites/dubai.sql.
    centroids: {
      dubai: { lat: 25.2048, lng: 55.2708 },
    },
  },
} as const satisfies SiteConfig;

export type SiteLocale = (typeof SITE.locales)[number];
